import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { store } from '../../store/store';
import { AppRoute } from '../../enums/appRoutes';
import { LoadingPage } from '../../pages/LoadingPage';
import { useRef, useEffect, FC, ReactElement } from 'react';
import { ApplianceDetailsSkeleton } from '../../pages/ApplianceDetailsPage/components/ApplianceDetailsSkeleton';
import { useAppState } from '../../store/selectors/useAppState';
import { useCheckout } from '../../store/selectors/useCheckout';
import { useConfirmation } from '../../store/selectors/useConfirmation';
import { useQuote } from '../../store/selectors/useQuote';
import { getLatestValidStepIndex } from '../../utils/store/getLatestValidStepIndex';
import { AppStatus } from '../../enums/appStatus';
import { StoreEvent } from '../../enums/storeEvent';
import { getRoutes } from '../../utils/router/getRoutes';

/**
 * @description The AppGuard component is responsible for guarding the routes of the application based on the current state of the app, quote, checkout, and confirmation.
 * It checks if the user has access to the route they are trying to access and redirects them accordingly.
 * It also handles the loading state of the app and displays a skeleton or loading page when necessary.
 *
 * @returns {ReactElement} The JSX element to render.
 */
export const AppGuard: FC = (): ReactElement => {
  const app = useAppState();
  const quote = useQuote();
  const checkout = useCheckout();
  const confirmation = useConfirmation();
  const location = useLocation();

  const { formStepRoutes } = getRoutes();

  /**
   * Placeholder effect and until we figure out how to properly handle session creation failures. This checks if they
   * are in an error state and if they have been redirected to the error page, if so it creates a new session when they
   * leave. We need this because currently if session creation fails, the app gets stuck in an error state and the user
   * has no way to recover from it.
   */
  const prevPathname = useRef(location.pathname);

  useEffect(() => {
    if (
      app.status === AppStatus.ERROR &&
      prevPathname.current === AppRoute.ERROR &&
      location.pathname !== AppRoute.ERROR
    ) {
      store.send({ type: StoreEvent.CREATE_SESSION });
    }
    prevPathname.current = location.pathname;
  }, [location.pathname]);

  if (app.status === AppStatus.LOADING) {
    return <ApplianceDetailsSkeleton />;
  }

  if (location.pathname === AppRoute.QUOTE && !quote?.data) {
    return <Navigate to={formStepRoutes[getLatestValidStepIndex()].path} replace />;
  }

  if (location.pathname === AppRoute.CHECKOUT) {
    if (checkout?.loading) return <LoadingPage />;
    if (!checkout?.data) {
      if (confirmation?.purchaseData) {
        return <LoadingPage />;
      }
      return quote?.data ? (
        <Navigate to={AppRoute.QUOTE} replace />
      ) : (
        <Navigate to={formStepRoutes[getLatestValidStepIndex()].path} replace />
      );
    }
  }

  if (location.pathname === AppRoute.CONFIRMATION) {
    if (!confirmation?.regOnlyData && !confirmation?.purchaseData) {
      return quote?.data ? (
        <Navigate to={AppRoute.QUOTE} replace />
      ) : (
        <Navigate to={formStepRoutes[getLatestValidStepIndex()].path} replace />
      );
    }
  }

  return <Outlet />;
};
